import { FastField } from 'formik'
import React from 'react'

import { PhotoThumbnail } from '@components/common/PhotoThumbnail'
import { Icon, Input, Tooltip, useMediaQueryContext } from '@fourthwall/components'
import { CloseIcon } from '@fourthwall/icons'
import { max } from '@fourthwall/utils/dist/validation'

import type { OrderItem } from '../../SamplesOrders.types'
import { QuantityDropdown } from '../QuantityDropdown/QuantityDropdown'
import { VariantPrice } from '../VariantPrice/VariantPrice'
import * as S from './SamplesOrdersListItem.styled'

type SamplesOrdersListProps = {
  orderItem: OrderItem
  index: number
  quantity: string
  onRemove: () => void
  onSetQuantity: (value: string) => void
}

export const SamplesOrdersListItem = ({
  orderItem,
  index,
  quantity,
  onRemove,
  onSetQuantity,
}: SamplesOrdersListProps) => {
  const media = useMediaQueryContext()

  const renderRemoveButton = () => (
    <S.RemoveButton onClick={onRemove}>
      <Icon component={CloseIcon} height={media.tablet ? 16 : 14} />
    </S.RemoveButton>
  )

  return (
    <S.Container>
      <S.Product>
        <PhotoThumbnail url={orderItem.image} size={media.tablet ? 'large' : 'medium'} />
        <S.ProductDetails>
          <div>{orderItem.name}</div>
          <S.Description>{orderItem.description}</S.Description>
          {!media.tablet && (
            <S.QuantityDropdownWrapper>
              Quantity:
              <QuantityDropdown
                title={`${orderItem.name}${orderItem.description ? `, ${orderItem.description}` : ''}`}
                description={
                  typeof orderItem.stock === 'number'
                    ? `${orderItem.stock} in stock`
                    : 'Unlimited stock'
                }
                quantity={quantity}
                maxValue={orderItem.stock}
                onSubmit={onSetQuantity}
              />
            </S.QuantityDropdownWrapper>
          )}
        </S.ProductDetails>
      </S.Product>
      {media.tablet && (
        <S.Quantity>
          <FastField
            component={Input}
            name={`items[${index}].quantity`}
            validate={
              orderItem.stock
                ? max(orderItem.stock, `Only ${orderItem.stock} in stock.`)
                : undefined
            }
            type="number"
            size="medium"
            fullWidth
            min={1}
            max={orderItem.stock}
          />
        </S.Quantity>
      )}
      <S.Price>
        {orderItem.price && orderItem.oldPrice && (
          <VariantPrice
            price={orderItem.price}
            oldPrice={orderItem.oldPrice}
            tooltip="You only pay the manufacturing price for samples"
          />
        )}
      </S.Price>
      <S.RemoveButtonWrapper>
        {media.tablet ? (
          <Tooltip reference={renderRemoveButton()} placement="bottom">
            Remove
          </Tooltip>
        ) : (
          <S.RemoveButton onClick={onRemove}>
            <Icon component={CloseIcon} height={media.tablet ? 16 : 14} />
          </S.RemoveButton>
        )}
      </S.RemoveButtonWrapper>
    </S.Container>
  )
}
